/**
 * EpiForms
 */

import React, { useReducer, useRef, useEffect } from 'react';

import { useForceUpdate, useFormRenderer, usePrevious } from 'hooks';
import { getInitialFormValues, getStepFields } from './EpiForms.helpers';
import { submitForm } from './EpiForms.submit';

import { Form } from 'components/Form';
import FormStep from './FormStep';
import FormMessage from './FormMessage';

export interface EpiFormsProps {
	/** Form id */
	id: string;

	/** Fields within the Form */
	fields: any;

	/** Form steps */
	steps: any[];

	/** Message to show when successfully submitted */
	confirmationMessage: string | null;

	/** Form method */
	method?: string;

	/** Form action */
	action?: string;

	/** Form encoding type */
	encodingType?: string;

	/** Form dependencies */
	dependencies?: any[];

	/** Form title (h1) */
	title?: string | null;

	/** Form description (p) */
	description?: string | null;

	/** Url to redirect to when successfully submitted */
	redirectUrl?: string;

	/** Epi properties for on-page editing */
	_properties?: any;

	[htmlAttributes: string]: any;
}

const initialState = {
	isLoading: false,
	invalidFields: [],
	validationMessages: {},
	successMessage: '',
	currentStep: 0,
	values: {},
	submissionId: '',
	serverValidationError: false,
};

const reducer = (state: any, action: any) => {
	switch (action.type) {
		case 'UPDATE_STATE':
			return { ...state, ...action.payload };
		default:
			return state;
	}
};

/** Rendering of an EpiForms block. */
const EpiForms: React.FC<EpiFormsProps> = ({
	fields,
	method = 'GET',
	action,
	encodingType = 'multipart/form-data',
	steps,
	dependencies,
	id,
	title,
	description,
	confirmationMessage,
	redirectUrl,
	_properties = {},
}) => {
	const [state, dispatch] = useReducer(reducer, initialState);
	const { currentStep, successMessage, submissionId, invalidFields } = state;

	const forceUpdate = useForceUpdate();
	const [renderFormElement, fieldActionExists] = useFormRenderer(
		fields,
		state,
		dependencies
	);

	const showForm = !confirmationMessage && !successMessage;
	const formValues = getInitialFormValues(fields, currentStep);

	const messageHolderRef = useRef<any>(null);
	const prevInvalidFields = usePrevious(invalidFields);
	let prevStep = usePrevious(currentStep);

	if (prevStep === undefined) {
		prevStep = 0;
	}

	// Focus messageHolderRef if success or error
	useEffect(() => {
		const successfullySubmitted =
			invalidFields.length > 0 || confirmationMessage || successMessage;
		const formMessageElement = messageHolderRef && messageHolderRef.current;

		if (
			successfullySubmitted &&
			formMessageElement &&
			prevInvalidFields !== invalidFields
		) {
			messageHolderRef.current.focus();
		}
	}, [invalidFields, messageHolderRef]);

	// Jump back to top if step changes
	useEffect(() => {
		if (window && prevStep !== currentStep && steps.length > 2) {
			window.location.href = `#${id}`;
		}
	}, [currentStep]);

	// If we have dependencies (conditional fields), we re-render.
	const onChange = (field: any, values: any) => {
		if ((dependencies && dependencies.length > 0) || fieldActionExists) {
			dispatch({
				type: 'UPDATE_STATE',
				payload: { values },
			});

			if (dependencies && dependencies.length > 0) {
				forceUpdate();
			}
		}
	};

	const onSubmit = (
		values: any,
		stepIndex: number,
		currentValidationMessages: any,
		currentInvalidFields: string[]
	) => {
		if (currentInvalidFields && currentInvalidFields.length > 0) {
			dispatch({
				type: 'UPDATE_STATE',
				payload: {
					invalidFields: currentInvalidFields,
					validationMessages: currentValidationMessages,
				},
			});
			return;
		}

		submitForm(values, stepIndex, dispatch, steps, {
			currentStep,
			submissionId,
			action,
			method,
			redirectUrl,
		});
	};

	return (
		<Form
			initialValues={formValues}
			sendDataType="formdata"
			method={method}
			action={action}
			enctype={encodingType}
			onSubmit={onSubmit}
			onChange={onChange}
			multiStep={steps.length > 2}
			currentStep={currentStep}
			getStepFields={getStepFields}
			id={id}
		>
			<header>
				{title && <h1 className="text-[2rem] leading-[3rem] md:text-[2.25rem] md:leading-[3.375rem] hyphens-auto break-words" {..._properties?.title}>{title}</h1>}

				{description && (
					<div>
						<p className="text-base text-[#595959]" {..._properties?.description}>
							{description}
						</p>
					</div>
				)}

				<FormMessage
					ref={messageHolderRef}
					confirmationMessage={confirmationMessage}
					state={state}
				/>
			</header>

			{showForm && (
				<div {..._properties?.fields}>
					{steps.map((step: any, i: number) =>
						step.index === -1 ? (
							<div id={`form-${id}-hiddenfields`} key="-1" hidden>
								{step.fields.map(renderFormElement)}
							</div>
						) : steps.length === 2 ? (
							step.fields.map(renderFormElement)
						) : (
							<FormStep
								key={i}
								step={step}
								nrOfSteps={steps.length}
								formId={id}
								currentStep={currentStep}
								onPrevious={(state: any) => {
									dispatch({
										type: 'UPDATE_STATE',
										payload: state,
									});
								}}
							>
								{step.fields.map(renderFormElement)}
							</FormStep>
						)
					)}
				</div>
			)}
		</Form>
	);
};

export default EpiForms;
